<template>
  <div class="image-container image-view">
      <img
          v-if="src"
          :id="imageId"
          :src="src"
          :class="['img-fluid', { 'm-auto': isFluid }]"
          :style="{ height: imgHeight, border: 'none', objectFit: 'contain' }"
          @load="onImageLoaded"
          @click="emitClick"
          alt="Photo"
      />
    <span
      v-if="loading"
      style="line-height: 15em"
    >
      {{ $t('common.loading') }}
    </span>
    <rotate-image-buttons
      v-if="(controls && !loading)"
      ref="rotateControls"
      :image-id="photoId"
      :control-size="controlSize"
      :image-container-id="imageId"
      :show-delete-button="showDeleteButton"
      :show-download-button="showDownloadButton"
      :photo-url="url"
      :photo-id="photoId"
      @save-clicked="saveRotation"
      @deleteImage="deleteImage"
      @download-image="handleDownload"
    />
  </div>
</template>

<script>
import {restApi} from '../mixins/RestApiMixin'
import {rotation} from '../mixins/ImageMixin'
import {utility} from '../mixins/Utils'
import { imageCacheService} from "@/services/ImageCacheService";
import RotateImageButtons from '@/components/view/RotateImageButtons'
import { downloadHelper } from '../mixins/DownloadMixin'

export default {
    name: 'ImageView',
    components: {RotateImageButtons},
    mixins: [restApi, rotation, utility, downloadHelper],
    props: {
        file: {
            type: Object,
            default: null
        },
        showDeleteButton: {
            type: Boolean,
            default: false
        },
        contain: {
            type: Boolean,
            default: false
        },
        isFluid: {
            type: Boolean,
            default: false
        },
        height: {
            type: String,
            default: 'auto'
        },
        url: {
            type: String,
            default() {
                return ''
            }
        },
        base64data: {
            type: String,
            default() {
                return ''
            }
        },
        imgSize: {
            type: String,
            default() {
                return 'md'
            }
        },
        controls: {
            type: Boolean,
            default() {
                return true
            }
        },
        controlSize: {
            type: String,
            default: 'md'
        },
        // Prop to control the image caching operation
        cachePhoto: {
            type: Boolean,
            default: false
        },
        cacheablePhotoId: {
            type: String,
            default: null
        },
        showDownloadButton: {
            type: Boolean,
            default: true
        },
    },
    data() {
        return {
            src: null,
            loading: false,
            placeholderClass: 'img-placeholder',
            initialClass: null,
            loaded: false,
            mounted: false,
            imageId: this.getUniqueId()
        }
    },
    computed: {
       imgHeight() {
            return this.height ? this.height : 'auto'
       },
       photoId() {
           return this.url.substring(this.url.lastIndexOf('/')+1)
       },
    },
    watch: {
        file(val) {
            if (this.mounted) {
                this.onFileChanged(val)
            }
        },
        url() {
            if (this.mounted) {
                this.onFileChanged(this.file)
            }
        }
    },
    mounted: function () {
        this.mounted = true
        this.onFileChanged(this.file)
    },
    methods: {
        onImageLoaded: function () {
            if (!this.loaded) {
                let selectedClass = this.contain ? 'image-view image-contain' : 'image-view'
                this.initialClass = selectedClass
                this.setImageClass(selectedClass)
                this.loaded = true
                this.$emit('imageLoaded')
            }
        },
        onFileChanged: function (file) {
            // Prefer base64 data if given
            if (this.base64data) {
                this.src = this.base64data
                return
            }

            if (!file) {
                if (this.isValidUrl(this.url)) {
                    // Restore the orientation for the file loaded from server
                    this.src = null
                    this.setImageClass(this.initialClass)
                    this.fetchPhoto(this.url)
                } else {
                    this.src = null
                }
            } else {
                // Restore default orientation
                this.setImageClass(this.initialClass)
                this.src = URL.createObjectURL(file)
            }
        },
        isValidUrl: function (url) {
            try {
                let u = new URL(url) // Gives lint warning, but must be kept
                if (u) {
                    return true
                }
            } catch (ex) {
                return false
            }
            return false
        },
        setImageClass: function (className) {
            var image = document.getElementById(this.imageId)
            if (image && className) {
                image.className = className
            }
        },
        fetchPhoto: function (url) {
            this.loading = true
            this.restFetch(url, this.handleResponse)
        },
        deleteImage() {
            this.$emit('deleteImage', this.photoId)
        },
        handleResponse: function (response) {
            this.loading = false
            if (response && response.status === 200) {
                this.src = response.data
                // making use of the new image cache service if the prop has been given for ImageView component
                // it will store the image data into the image cache and makes it usable where needed without unnecessary emits and api calls
                if(this.cachePhoto && this.cacheablePhotoId) {
                    imageCacheService.set(this.cacheablePhotoId.toString(), response.data);
                }
            }
        },
        emitClick: function () {
            this.$emit('click', this.src);
        },
        saveRotation(rotation) {
            this.$refs.rotateControls.resetRotation()
            this.src = null
            this.initialClass = null
            this.loading = true
            this.restUpdate(this.photoRotationUrl + '/' + this.photoId + '/' + rotation, {},
                this.rotationSaveResponse, this.rotationSaveResponse)
        },
        rotationSaveResponse() {
            // Re-fetch photo to have the rotated photo shown
            this.onFileChanged(this.file)
        },
        handleDownload({ id }) {
            const [header, data] = this.src.split(',')
            const mime = header.split(':')[1].split(';')[0]
            const bytes = Uint8Array.from(atob(data), c => c.charCodeAt(0))
            this.downloadFile({ data: new Blob([bytes], { type: mime }), headers: {} }, `photo_${id}.jpg`)
        }

    }
}
</script>
<style>
@import "../../styles/rotate.css";
</style>
<style scoped>
.image-container {
    position: relative;
    height: 100%;
    display: flex;
    width: auto;
    text-align: center;
}
.image-view {
    Transition: transform 0.3s ease;
}
</style>
